import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, qs, onClick, toast, confirmDialog } from '../dom.js';
import { icon } from '../icons.js';
import { loadLayout } from '../../forms.js';
import { scoreResponse } from '../../scoring/score.js';
import { answerRowHtml, answerGridHtml } from '../answerGrid.js';
import type { ResultRecord } from '../../db/schema.js';

const pct = (a: number, b: number) => (b ? Math.round((100 * a) / b) : 0);

export async function resultDetailView(params: Record<string, string>): Promise<RenderedView> {
  const result = await repos.getResult(params.id);
  if (!result) return { title: 'Result', back: '/quizzes', html: `<div class="view"><p class="empty">Result not found.</p></div>` };
  const assignment = (await repos.getAssignment(result.quizAssignmentId))!;
  const quiz = (await repos.getQuiz(assignment.quizId))!;
  const [layout, key, students] = await Promise.all([
    loadLayout(quiz.items),
    repos.answerKeyFor(quiz.id),
    repos.studentsInClass(assignment.classId),
  ]);
  const opts = layout.quiz.options;

  // mutable working copy
  const answers = result.rawAnswers.map((a) => ({ ...a, flags: [...a.flags] }));
  let assignedId = result.studentId ?? '';
  const mode = assignment.identificationMode ?? 'roster';
  const captured = mode === 'captured_name';
  // lrn_direct: the sheet bubbles the 12-digit LRN with no roster, so identity is
  // the read digits — the teacher corrects THOSE, not a roster pick.
  const lrnDirect = mode === 'lrn_direct';
  let capturedName = result.capturedName ?? '';
  let lrnEdited = result.idDigitsRead ?? '';
  const score = () => (key ? scoreResponse(answers, key) : null);

  const rowHtml = (a: ResultRecord['rawAnswers'][number]) => answerRowHtml(a, opts, key);

  const sc = score();
  const studentOptions = `<option value="">— unassigned —</option>` +
    students.map((s) => `<option value="${esc(s.id)}" ${s.id === assignedId ? 'selected' : ''}>${esc(s.name)} (${esc(s.lrn)})</option>`).join('');

  return {
    title: 'Sheet',
    subtitle: quiz.title,
    back: `/assignment/${assignment.id}/review`,
    html: `<div class="view view-flush">
      ${key && sc ? `<div class="score-banner" id="banner"><span class="big">${sc.score}/${sc.maxScore}</span>
        <span class="grow">${pct(sc.score, sc.maxScore)}%${sc.reviewItems.length ? ` · <span class="danger-text">${sc.reviewItems.length} to review</span>` : ''}</span></div>`
        : `<div class="card"><span class="badge badge-warn">No answer key set</span></div>`}
      ${captured
        ? `<div class="card"><div class="field" style="margin:0"><label for="cname">Student name <span class="muted">(from the scanned sheet)</span></label>
             ${result.capturedNameImage ? `<div class="name-crop"><img id="cropimg" alt="Scanned name"></div>` : ''}
             <input class="input" id="cname" autocomplete="off" value="${esc(capturedName)}"></div></div>`
        : lrnDirect
          ? `<div class="card"><div class="field" style="margin:0"><label for="lrn">LRN <span class="muted">(read from the sheet — correct if unclear)</span></label>
               <input class="input mono" id="lrn" inputmode="numeric" maxlength="12" autocomplete="off" value="${esc(lrnEdited)}" placeholder="12-digit LRN">
               <p class="hint">Must be exactly 12 digits to upload to TESTIFY.</p></div></div>`
          : `<div class="card"><div class="field" style="margin:0"><label for="stu">Student <span class="muted">(read "${esc(result.idDigitsRead || '—')}")</span></label>
             ${result.capturedNameImage ? `<div class="name-crop"><img id="cropimg" alt="Scanned name box"></div><p class="hint">LRN wasn't matched — pick the student by the written name above.</p>` : ''}
             <select class="input" id="stu">${studentOptions}</select></div></div>`}
      ${result.syncStatus === 'rejected' && result.syncError
        ? `<div class="card"><span class="badge badge-danger">${icon.alert()} TESTIFY rejected this upload</span><p class="hint" style="margin-top:8px">${esc(result.syncError)}</p></div>`
        : result.syncStatus === 'synced'
          ? `<div class="card"><span class="badge badge-ok">${icon.check()} Uploaded to TESTIFY</span></div>`
          : ''}
      <p class="section-label">Answers · tap to correct</p>
      <div class="answers-grid" id="grid">${answerGridHtml(answers, opts, key)}</div>
      <button class="btn btn-danger btn-block" data-delete style="margin-top:18px">${icon.trash()} Delete this result</button>
    </div>`,
    bottom: `<a class="btn btn-ghost" href="#/assignment/${assignment.id}/scan">${icon.refresh()} Re-scan</a>
      <button class="btn btn-primary" data-save>${icon.check()} Save</button>`,
    mount(root) {
      // The name-box crop (#cropimg) appears in captured_name mode and in roster
      // mode when the LRN didn't match — render it wherever it's present.
      if (result.capturedNameImage) {
        const img = qs<HTMLImageElement>(root, '#cropimg');
        if (img) img.src = URL.createObjectURL(result.capturedNameImage);
      }
      if (captured) {
        qs<HTMLInputElement>(root, '#cname')!.addEventListener('input', (e) => (capturedName = (e.target as HTMLInputElement).value));
      } else if (lrnDirect) {
        const lrnEl = qs<HTMLInputElement>(root, '#lrn')!;
        lrnEl.addEventListener('input', (e) => {
          const raw = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 12);
          lrnEl.value = raw; // keep it numeric as they type
          lrnEdited = raw;
        });
      } else {
        qs<HTMLSelectElement>(root, '#stu')!.addEventListener('change', (e) => (assignedId = (e.target as HTMLSelectElement).value));
      }
      qs(root, '#grid')!.addEventListener('click', (e) => {
        const el = (e.target as HTMLElement).closest('.opt') as HTMLElement | null;
        if (!el) return;
        const q = Number(el.dataset.q);
        const a = answers.find((x) => x.q === q)!;
        a.chosen = a.chosen === el.dataset.opt ? null : el.dataset.opt!;
        a.flags = [];
        qs(root, `.qrow[data-row="${q}"]`)!.outerHTML = rowHtml(a);
        const banner = qs(root, '#banner');
        const s2 = score();
        if (banner && s2) {
          banner.querySelector('.big')!.textContent = `${s2.score}/${s2.maxScore}`;
          banner.querySelector('.grow')!.innerHTML = `${pct(s2.score, s2.maxScore)}%${s2.reviewItems.length ? ` · <span class="danger-text">${s2.reviewItems.length} to review</span>` : ''}`;
        }
      });
      onClick(root, '[data-save]', async () => {
        const s2 = score();
        const name = capturedName.trim();
        const lrn = lrnEdited.trim();
        // Identity per mode: captured name, an lrn_direct 12-digit read, or a roster pick.
        const identified = captured ? !!name : lrnDirect ? /^\d{12}$/.test(lrn) : !!assignedId;
        // In lrn_direct mode the corrected digits become the sheet's LRN. If the
        // teacher's read maps to a roster student in this class, link it too.
        const matchedByLrn = lrnDirect && /^\d{12}$/.test(lrn) ? await repos.findByLrn(assignment.classId, lrn) : undefined;
        // A TESTIFY result that was already uploaded (or rejected) is now stale
        // after an edit — re-queue it so the correction reaches TESTIFY on the
        // next upload. 'local_only' results stay local.
        const wasUploadable = result.syncStatus === 'synced' || result.syncStatus === 'rejected' || result.syncStatus === 'pending';
        const updated: ResultRecord = {
          ...result,
          rawAnswers: answers,
          idDigitsRead: lrnDirect ? lrn : result.idDigitsRead,
          studentId: captured ? null : lrnDirect ? matchedByLrn?.id ?? null : assignedId || null,
          capturedName: captured ? name || null : result.capturedName ?? null,
          score: s2 ? s2.score : null,
          maxScore: s2 ? s2.maxScore : null,
          needsReview: !identified || answers.some((a) => a.flags.length > 0),
          syncStatus: wasUploadable ? 'pending' : result.syncStatus,
          syncError: wasUploadable ? undefined : result.syncError,
        };
        await repos.saveResult(updated);
        if (lrnDirect && lrn && !/^\d{12}$/.test(lrn)) toast('Saved, but the LRN needs 12 digits to upload', 'err');
        else toast(wasUploadable && result.syncStatus === 'synced' ? 'Saved — will re-upload to TESTIFY' : 'Saved');
        navigate(`#/assignment/${assignment.id}/review`);
      });
      onClick(root, '[data-delete]', async () => {
        if (!confirmDialog('Delete this result permanently?')) return;
        await repos.deleteResult(result.id);
        toast('Deleted');
        navigate(`#/assignment/${assignment.id}/review`);
      });
    },
  };
}
